// src/components/dashboard/NotificationBell.js
import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot, updateDoc, doc } from "firebase/firestore";
import { db } from "../../firebase";
import { acceptHelperRequest, declineHelperRequest } from "../../services/problemService";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Bell, Check, X } from "lucide-react";

function NotificationBell({ currentUser }) {
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [processingRequest, setProcessingRequest] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) return;

    // Subscribe to notifications (you'll need to create this collection)
    const notifRef = collection(db, "notifications");
    const q = query(
      notifRef,
      where("userId", "==", currentUser.uid),
      where("read", "==", false)
    );

    const unsub = onSnapshot(q, (snapshot) => {
      const notifs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setNotifications(notifs);
      setUnreadCount(notifs.length);
    });

    return () => unsub();
  }, [currentUser]);

  const markAsRead = async (notifId) => {
    try {
      await updateDoc(doc(db, "notifications", notifId), {
        read: true,
      });
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  const markAllAsRead = async () => {
    try {
      for (const notif of notifications) {
        await markAsRead(notif.id);
      }
    } catch (err) {
      console.error("Error marking all as read:", err);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center h-4 min-w-[1rem] px-1 text-[10px] font-bold leading-none text-white bg-rose-600 rounded-full">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg border border-slate-200 z-50">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Mark all as read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm text-slate-500">No new notifications</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  className="p-4 border-b border-slate-100 hover:bg-slate-50"
                >
                  <p className="text-sm text-slate-900 mb-2">{notif.message}</p>
                  <p className="text-xs text-slate-500 mb-3">
                    {notif.createdAt?.toDate().toLocaleString()}
                  </p>
                  
                  {/* Accept/Decline buttons for helper requests */}
                  {notif.type === "helper-request" && notif.problemId && notif.helperId && (
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (processingRequest === notif.id) return;
                          
                          setProcessingRequest(notif.id);
                          try {
                            const result = await acceptHelperRequest(
                              notif.problemId,
                              notif.helperId,
                              currentUser.uid
                            );
                            
                            if (result.success) {
                              toast.success(`${notif.helperName || 'Helper'} has been assigned to your problem.`);
                              await markAsRead(notif.id);
                              // Navigate to workspace after a short delay
                              setTimeout(() => {
                                navigate(`/workspace/${notif.problemId}`);
                              }, 1000);
                            } else {
                              toast.error(result.error || "Failed to accept request");
                            }
                          } catch (error) {
                            console.error("Error accepting helper request:", error);
                            toast.error("Failed to accept request: " + error.message);
                          } finally {
                            setProcessingRequest(null);
                          }
                        }}
                        disabled={processingRequest === notif.id}
                        className="flex-1 flex items-center justify-center gap-1 bg-emerald-600 text-white px-3 py-1.5 rounded text-xs font-medium hover:bg-emerald-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {processingRequest === notif.id ? (
                          "Processing..."
                        ) : (
                          <>
                            <Check className="w-3.5 h-3.5" />
                            Accept
                          </>
                        )}
                      </button>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (processingRequest === notif.id) return;
                          
                          setProcessingRequest(notif.id);
                          try {
                            const result = await declineHelperRequest(
                              notif.problemId,
                              notif.helperId,
                              currentUser.uid
                            );
                            
                            if (result.success) {
                              toast.info("Request declined");
                              await markAsRead(notif.id);
                            } else {
                              toast.error(result.error || "Failed to decline request");
                            }
                          } catch (error) {
                            console.error("Error declining helper request:", error);
                            toast.error("Failed to decline request: " + error.message);
                          } finally {
                            setProcessingRequest(null);
                          }
                        }}
                        disabled={processingRequest === notif.id}
                        className="flex-1 flex items-center justify-center gap-1 bg-rose-600 text-white px-3 py-1.5 rounded text-xs font-medium hover:bg-rose-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {processingRequest === notif.id ? (
                          "Processing..."
                        ) : (
                          <>
                            <X className="w-3.5 h-3.5" />
                            Decline
                          </>
                        )}
                      </button>
                    </div>
                  )}
                  
                  {/* For other notification types, make clickable to mark as read */}
                  {notif.type !== "helper-request" && (
                    <button
                      onClick={() => markAsRead(notif.id)}
                      className="text-xs text-blue-600 hover:text-blue-800 mt-2"
                    >
                      Mark as read
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;

